import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { format, addDays } from "date-fns";
import PortalModal from "../components/PortalModal";
import "./ZReport.css";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function calculateHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let hours = eh + em / 60 - (sh + sm / 60);
  if (hours < 0) hours += 24;
  return Math.round(hours * 100) / 100;
}

// Toast with icon and style matching Clockins
function Toast({ message, type, onClose }) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onClose, 2500);
    return () => clearTimeout(timer);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className="toast-success">
      <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#1a7f37"/><path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      {message}
    </div>
  );
}

export default function RotaAdmin() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Entry form state
  const [form, setForm] = useState({
    id: null,
    day: 0,
    date: format(getMonday(new Date()), "yyyy-MM-dd"),
    shiftTypeId: "",
    employeeId: "",
    start: "06:00",
    end: "12:00"
  });
  const [isEditing, setIsEditing] = useState(false);

  // Shift type modal state
  const [shiftModal, setShiftModal] = useState({ open: false, mode: "add", id: null, name: "", order_num: 1 });
  const [selectedShiftTypeId, setSelectedShiftTypeId] = useState("");

  // Toast state
  const [toast, setToast] = useState({ message: "", type: "success" });

  // Fetch employees
  useEffect(() => {
    supabase.from("employees").select("*").then(({ data }) => setEmployees(data || []));
  }, []);

  // Set default employeeId in form when employees load
  useEffect(() => {
    if (employees.length && !form.employeeId) {
      setForm(f => ({ ...f, employeeId: employees[0].id }));
    }
  }, [employees]);

  // Fetch shift types
  const fetchShiftTypes = async () => {
    const { data } = await supabase
      .from("shift_types")
      .select("*")
      .order("order_num", { ascending: true });
    setShiftTypes(data || []);
    if (data && data.length && !form.shiftTypeId) {
      setForm(f => ({ ...f, shiftTypeId: data[0].id }));
    }
    if (data && data.length && !selectedShiftTypeId) {
      setSelectedShiftTypeId(data[0].id);
    }
  };

  useEffect(() => {
    fetchShiftTypes();
    // eslint-disable-next-line
  }, []);

  // Set default shiftTypeId in form when shiftTypes load
  useEffect(() => {
    if (shiftTypes.length && !form.shiftTypeId) {
      setForm(f => ({ ...f, shiftTypeId: shiftTypes[0].id }));
    }
  }, [shiftTypes]);

  // Fetch assignments for week
  useEffect(() => {
    fetchAssignmentsForWeek(weekStart);
    // eslint-disable-next-line
  }, [weekStart, shiftTypes]);

  const fetchAssignmentsForWeek = async (weekStartDate) => {
    const week_start_str = weekStartDate.toISOString().slice(0, 10);
    const { data } = await supabase
      .from("rota_assignment")
      .select("*")
      .eq("week_start", week_start_str);
    setAssignments((data || []).map(a => ({
      id: a.id,
      day: a.day,
      shiftTypeId: a.shift_type_id,
      employeeId: a.employee_id,
      start: a.shift_start,
      end: a.shift_end,
      hours: a.hours
    })));
  };

  // Helper to get shift type name by id
  const getShiftTypeName = (id) => shiftTypes.find(t => t.id === id)?.name || "";

  // Helper to get employee name by id
  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || "";

  // Handle form change
  const handleFormChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === "day") {
      const date = format(addDays(weekStart, Number(value)), "yyyy-MM-dd");
      setForm(f => ({ ...f, date }));
    }
  };

  // Add or Edit assignment
const handleAddOrEdit = async () => {
  if (!form.shiftTypeId || !form.employeeId || !form.start || !form.end) {
    setToast({ message: "Please fill all fields.", type: "error" });
    return;
  }
  if (isEditing) {
    await supabase.from("rota_assignment").update({
      employee_id: form.employeeId,
      day: form.day,
      shift_type_id: form.shiftTypeId,
      shift_name: getShiftTypeName(form.shiftTypeId),
      shift_start: form.start,
      shift_end: form.end,
      hours: calculateHours(form.start, form.end)
    }).eq("id", form.id);
    setToast({ message: "Assignment updated!", type: "success" });
  } else {
    const week_start_str = weekStart.toISOString().slice(0, 10);
    const insertData = {
      employee_id: form.employeeId,
      week_start: week_start_str,
      day: form.day,
      shift_type_id: form.shiftTypeId,
      shift_name: getShiftTypeName(form.shiftTypeId),
      shift_start: form.start,
      shift_end: form.end,
      hours: calculateHours(form.start, form.end)
    };
    console.log("Insert data:", insertData);
    const { error } = await supabase.from("rota_assignment").insert([insertData]);
    if (error) {
      console.error("Supabase insert error:", error);
      setToast({ message: error.message || "Error adding assignment.", type: "error" });
      return;
    }
    setToast({ message: "Assignment added!", type: "success" });
  }
  fetchAssignmentsForWeek(weekStart);
  setForm({
    id: null,
    day: 0,
    date: format(weekStart, "yyyy-MM-dd"),
    shiftTypeId: shiftTypes[0]?.id || "",
    employeeId: employees[0]?.id || "",
    start: "06:00",
    end: "12:00"
  });
  setIsEditing(false);
};
  // Edit assignment (populate form)
  const handleEdit = (a) => {
    setForm({
      id: a.id,
      day: a.day,
      date: format(addDays(weekStart, a.day), "yyyy-MM-dd"),
      shiftTypeId: a.shiftTypeId,
      employeeId: a.employeeId,
      start: a.start,
      end: a.end
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Remove assignment (from DB and UI)
  const handleRemove = async (id) => {
    await supabase.from("rota_assignment").delete().eq("id", id);
    setAssignments(prev => prev.filter(a => a.id !== id));
    setToast({ message: "Assignment deleted!", type: "success" });
    fetchAssignmentsForWeek(weekStart);
  };

  // Save to backend (not needed, as each add/delete is instant, but keep for batch save if you want)
  const handleSave = async () => {
    setLoading(true);
    const week_start_str = weekStart.toISOString().slice(0, 10);
    await supabase.from("rota_assignment").delete().eq("week_start", week_start_str);
    if (assignments.length) {
      await supabase.from("rota_assignment").insert(assignments.map(a => ({
        employee_id: a.employeeId,
        week_start: week_start_str,
        day: a.day,
        shift_type_id: a.shiftTypeId,
        shift_name: getShiftTypeName(form.shiftTypeId),
        shift_start: a.start,
        shift_end: a.end,
        hours: calculateHours(a.start, a.end)
      })));
    }
    setLoading(false);
    fetchAssignmentsForWeek(weekStart);
    setToast({ message: "Rota saved successfully!", type: "success" });
  };

  // Copy previous week
const handleCopyPreviousWeek = async () => {
  const prevWeekStart = new Date(weekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
  const prevWeekStr = prevWeekStart.toISOString().slice(0, 10);
  const currWeekStr = weekStart.toISOString().slice(0, 10);

  const { data: prevAssignments, error: fetchError } = await supabase
    .from("rota_assignment")
    .select("*")
    .eq("week_start", prevWeekStr);

  if (fetchError) {
    setToast({ message: "Error fetching previous week.", type: "error" });
    return;
  }

  if (!prevAssignments || prevAssignments.length === 0) {
    setToast({ message: "No assignments found for previous week.", type: "error" });
    return;
  }

  // Remove all for this week, then insert
  await supabase.from("rota_assignment").delete().eq("week_start", currWeekStr);

  // Ensure all required fields are present
  const newAssignments = prevAssignments.map(a => ({
    employee_id: a.employee_id,
    week_start: currWeekStr,
    day: a.day,
    shift_type_id: a.shift_type_id,
    shift_name: a.shift_name, // <-- make sure this is included!
    shift_start: a.shift_start,
    shift_end: a.shift_end,
    hours: a.hours
  }));

  const { error: insertError } = await supabase.from("rota_assignment").insert(newAssignments);

  if (insertError) {
    setToast({ message: "Error copying previous week.", type: "error" });
    return;
  }

  await fetchAssignmentsForWeek(weekStart);
  setToast({ message: "Copied previous week!", type: "success" });
};



  // Shift type management
  const handleShiftTypeSave = async () => {
    if (!shiftModal.name.trim()) return;
    if (shiftModal.mode === "add") {
      await supabase.from("shift_types").insert([{name: shiftModal.name, order_num: shiftTypes.length + 1 }]);
    } else if (shiftModal.mode === "edit") {
      await supabase.from("shift_types").update({
  name: shiftModal.name
}).eq("id", shiftModal.id);
    }
    setShiftModal({ open: false, mode: "add", id: null, name: "", order_num: 1 });
    fetchShiftTypes();
    setToast({ message: "Shift type saved!", type: "success" });
  };

  const handleShiftTypeRemove = async (id) => {
    if (!window.confirm("Delete this shift type?")) return;
    await supabase.from("shift_types").delete().eq("id", id);
    fetchShiftTypes();
    setToast({ message: "Shift type deleted!", type: "success" });
  };

  // Shift type dropdown for edit/add
  const nextShiftNum = shiftTypes.length + 1;

  return (
    <div className="main-content zreport-container">
      <div className="main-heading">Rota Admin for week starting {format(weekStart, "dd/MM/yyyy")}</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className="btn-apply" onClick={() => setWeekStart(getMonday(new Date()))}>Current Week</button>
        <button className="btn-apply" onClick={() => setWeekStart(addDays(weekStart, -7))}>Prev</button>
        <button className="btn-apply" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next</button>
        <button className="btn-apply" onClick={handleCopyPreviousWeek}>Copy Previous Week</button>
      </div>

      {/* Assignment Entry Form */}
      <div className="section-box">
        <form
          style={{
            display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center"
          }}
          onSubmit={e => { e.preventDefault(); handleAddOrEdit(); }}
        >
          <label>
            Day
            <select
              value={form.day}
              onChange={e => handleFormChange("day", Number(e.target.value))}
              style={{ minWidth: 120 }}
            >
              {days.map((d, idx) => (
                <option key={d} value={idx}>{d}</option>
              ))}
            </select>
          </label>
          <label>
            Date
            <input
              type="date"
              value={form.date}
              disabled
              style={{ minWidth: 120 }}
            />
          </label>
          <label>
            Shift Type
            <select
              value={form.shiftTypeId}
              onChange={e => handleFormChange("shiftTypeId", e.target.value)}
              style={{ minWidth: 120 }}
            >
              {shiftTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
            </select>
          </label>
          <label>
            Employee
            <select
              value={form.employeeId}
              onChange={e => handleFormChange("employeeId", e.target.value)}
              style={{ minWidth: 120 }}
            >
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
          <label>
            Start
            <input
              type="time"
              value={form.start}
              onChange={e => handleFormChange("start", e.target.value)}
              style={{ minWidth: 100 }}
            />
          </label>
          <label>
            End
            <input
              type="time"
              value={form.end}
              onChange={e => handleFormChange("end", e.target.value)}
              style={{ minWidth: 100 }}
            />
          </label>
          <button className="btn-apply" type="submit" style={{ minWidth: 120 }}>
            {isEditing ? "Save Changes" : "Add Assignment"}
          </button>
          {isEditing && (
            <button
              className="btn-apply"
              type="button"
              style={{ background: "#eee", color: "#222", minWidth: 120 }}
              onClick={() => {
                setForm({
                  id: null,
                  day: 0,
                  date: format(weekStart, "yyyy-MM-dd"),
                  shiftTypeId: shiftTypes[0]?.id || "",
                  employeeId: employees[0]?.id || "",
                  start: "06:00",
                  end: "12:00"
                });
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          )}
        </form>
      </div>

      {/* Shift Types Management */}
      <div className="section-box">
        <h3>Shift Types</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <select
            value={selectedShiftTypeId}
            onChange={e => setSelectedShiftTypeId(e.target.value)}
            style={{ minWidth: 120, marginRight: 8 }}
          >
            {shiftTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
          <button className="btn-apply" onClick={() => {
            const type = shiftTypes.find(t => t.id === selectedShiftTypeId);
            setShiftModal({
              open: true,
              mode: "edit",
              id: type.id,
              name: type.name,
              order_num: type.order_num
            });
          }}>Edit</button>
          <button className="btn-apply" style={{ marginLeft: 8 }} onClick={() => {
            setShiftModal({
              open: true,
              mode: "add",
              id: null,
              name: `Shift ${nextShiftNum}`,
              order_num: nextShiftNum
            });
          }}>+ Add Shift Type</button>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="section-box">
        <table className="rota-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Day</th>
              <th>Date</th>
              <th>Shift Type</th>
              <th>Employee</th>
              <th>Start</th>
              <th>End</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && (
              <tr>
                <td colSpan={8} style={{ color: "#aaa", textAlign: "center" }}>No assignments for this week.</td>
              </tr>
            )}
            {assignments
              .sort((a, b) => a.day - b.day || getShiftTypeName(a.shiftTypeId).localeCompare(getShiftTypeName(b.shiftTypeId)))
              .map(a => (
                <tr key={a.id}>
                  <td>{days[a.day]}</td>
                  <td>{format(addDays(weekStart, a.day), "dd/MM")}</td>
                  <td>{getShiftTypeName(a.shiftTypeId)}</td>
                  <td>{getEmployeeName(a.employeeId)}</td>
                  <td>{a.start}</td>
                  <td>{a.end}</td>
                  <td>
                    <button className="btn-apply" onClick={() => handleEdit(a)}>Edit</button>
                  </td>
                  <td>
                    <button className="btn-delete" onClick={() => handleRemove(a.id)}>Delete</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <button className="btn-apply" style={{ marginTop: 24 }} onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save Rota"}
        </button>
      </div>

      {/* Shift Type Modal using PortalModal */}
      {shiftModal.open && (
        <PortalModal>
          <div className="modal-dark">
            <div className="modal-content-dark">
              <div style={{ marginBottom: 16 }}>
                <b>{shiftModal.mode === "add" ? "Add Shift Type" : "Edit Shift Type"}</b>
              </div>
              <input
                value={shiftModal.name}
                onChange={e => setShiftModal(m => ({ ...m, name: e.target.value }))}
                placeholder="Shift Name"
                style={{ width: "100%", marginBottom: 8 }}
              />
              <label>
                </label>
              <div style={{ textAlign: "right", marginTop: 16 }}>
                <button className="btn-apply" onClick={handleShiftTypeSave}>Save</button>
                <button className="btn-apply" style={{ marginLeft: 8, background: "#eee", color: "#222" }}
                  onClick={() => setShiftModal({ open: false, mode: "add", id: null, name: "", order_num: 1 })}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </PortalModal>
      )}

      {/* Toast */}
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />
    </div>
  );
}